import {
  isAnswerCorrect,
  isAnswerPartial,
  numericListPartialCredit,
  submissionIsDeterministicallyWrong,
  submittedMissesPercentSign,
} from "../../../utils/answerMatchUtils";
import { isFillQuestionType, parsePipeJoinedBlanks } from "../../../utils/fillQuestionUtils";

// The Result page and the Review page render the same leaf rows from the same server context, but each
// used to re-derive its own verdict and marks. They drifted: only Result applied the equivalence
// upgrade, only Review let the AI overturn a rule verdict on a non-text row, and Review ignored the
// server partial_ratio (flat 0.5 for every partial). This resolver is the Result page's semantics —
// rule verdict first, AI consulted only for free-text rows — used by both pages so they agree.

const TEXT_TYPE_HINTS = ["short_answer", "essay", "short answer", "text"];

const normType = (row) => String(row.question_type || "").toLowerCase().replace(/_/g, "-");

const rowIsText = (row) => {
  if (typeof row.is_text === "boolean") return row.is_text;
  const qt = normType(row);
  return TEXT_TYPE_HINTS.some((t) => qt.includes(t));
};

const rowIsAnswered = (row) => {
  if (row.answered === false) return false;
  if (row.answered === true) return true;
  return (row.your_answer != null && String(row.your_answer).trim() !== "")
    || !!row.your_answer_drawing
    || !!row.your_answer_interactive;
};

// Unit/currency/ordinal noise the grader is stricter about than a human marker would be. Kept narrow:
// it only ever upgrades an exact match after normalisation, and never runs once detWrong fires.
const equivalentAfterNormalisation = (submitted, correct) => {
  if (!submitted || !correct) return false;
  const norm = (v) => String(v ?? "").toLowerCase().trim()
    .replace(/÷/g, "/")
    .replace(/[£$€°]/g, "").replace(/\s*(am|pm)\s*$/i, "")
    .replace(/^(\d{1,2})\.(\d{2})$/, "$1:$2")
    .replace(/^(\d+)(?:st|nd|rd|th)(?:\s+\w+)*$/, "$1")
    .replace(/^(\d+(?:\.\d+)?)\s*p$/, "$1")
    .replace(/\s*\b(?:km|cm|mm|mg|kg|ml|litres?|liters?|metres?|meters?)\s*$/i, "")
    .trim();
  const alts = String(correct).includes(" (or) ")
    ? String(correct).split(" (or) ").map((p) => p.trim())
    : [String(correct)];
  return alts.some((a) => norm(a) === norm(submitted));
};

export const resolveLeafVerdict = (row, { aiFeedbackMap, aiFeedbackStatus } = {}) => {
  const max = Number(row.marks ?? 0);
  const isText = rowIsText(row);
  const answered = rowIsAnswered(row);

  const blankCount = row.answer_data?.correct_blanks?.length
    || row.answer_data?.blank_labels?.length
    || parsePipeJoinedBlanks(row.correct_answer_label).length;
  // image-fill is a multi-blank fill too — isFillQuestionType leaves it out because the display code
  // renders it through its own overlay path, not because the server grades it any differently.
  const isMultiBlankFill = (isFillQuestionType(normType(row)) || normType(row) === "image-fill")
    && blankCount > 1;

  // "Answer must be written exactly like this" — the server compared it literally, so neither the
  // fuzzy client fallback nor the normalisation upgrade below may reopen the verdict.
  const exactOnly = !!(row.exact_match || row.answer_data?.exact_match);

  const ruleIsCorrect = row.is_correct == null
    ? (!exactOnly && !isText && !isMultiBlankFill && isAnswerCorrect(row.your_answer, row.correct_answer_label, { questionText: row.question_text }))
    : !!row.is_correct;
  const ruleIsPartial = !ruleIsCorrect
    && (!!row.is_partial
      || (row.is_correct == null && !exactOnly && !isText && !isMultiBlankFill && isAnswerPartial(row.your_answer, row.correct_answer_label)));

  // Multi-blank fills are excluded for the reason StudentPaperTake already excludes them: `your_answer`
  // is the boxes joined with "|" and `correct_answer_label` is a display string listing every box's
  // value, so a whole-answer guard reads the two lists as ONE value. answerSetMatchesExactly does not
  // even count "|" as a separator on the submitted side, so it tore "39 | 187 | 112" into 5 tokens and
  // called every pipe-joined fill deterministically wrong — a fully correct one included. That verdict
  // then overrode the server's, which had already graded each box (pooled rows, answer_variants and
  // partial credit included — none of which a single-string client matcher can model).
  const detWrong = !isMultiBlankFill && !!(row.your_answer && row.correct_answer_label
    && (submissionIsDeterministicallyWrong(row.your_answer, row.correct_answer_label, row.question_text)
      || submittedMissesPercentSign(row.your_answer, row.correct_answer_label, row)));

  // A value-set list ("2, 3, 5, 7, …") that misses one value or repeats one is deterministically wrong
  // as a whole answer — that is exactly why it lands here as a flat Incorrect. Score the values instead.
  const listPartial = (!isMultiBlankFill && !exactOnly && !ruleIsCorrect)
    ? numericListPartialCredit(row.your_answer, row.correct_answer_label)
    : null;

  const aiq = aiFeedbackMap?.[String(row.question_id)];
  // The AI only ever decides a free-text row the rules could not grade. Letting it overturn a graded
  // non-text row is what made the two pages disagree, and it re-opens every guard the rule matcher owns.
  const aiReady = isText && !detWrong && !exactOnly && aiFeedbackStatus === "completed" && !!aiq;
  const aiRatio = aiq?.partial_score != null ? Number(aiq.partial_score) : null;
  const aiIsPartial = aiRatio != null && aiRatio > 0 && aiRatio < 1;

  const isEquiv = !ruleIsCorrect && !isText && !detWrong && !exactOnly
    && equivalentAfterNormalisation(row.your_answer, row.correct_answer_label);

  const correct = !detWrong && answered
    && (ruleIsCorrect || isEquiv || (aiReady && aiq.is_correct === true));
  // A text row's ruleIsPartial can only be the SERVER's verdict (the client branch above is already
  // excluded for them), and the server is exactly who scored "triangle" for "Equilateral triangle" as
  // half a mark — dropping it here is how that partial became a zero after submit.
  const partial = !correct && answered
    && (!!listPartial || (!detWrong && (ruleIsPartial || (aiReady && aiIsPartial))));

  if (!answered) return { result: "unanswered", earned: 0, max, isEquiv: false };
  if (correct) return { result: "correct", earned: max, isEquiv: isEquiv && !ruleIsCorrect, max };
  if (partial) {
    const serverRatio = Number(row.partial_ratio ?? 0);
    const ratio = Math.max(serverRatio, aiIsPartial ? aiRatio : 0, listPartial?.ratio ?? 0) || 0.5;
    return { result: "partial", earned: Math.round(ratio * max * 100) / 100, max, isEquiv: false };
  }
  // An exact_match row was graded literally by the server — it is never "awaiting a human/AI read".
  if (isText && !detWrong && !aiReady && !exactOnly) return { result: "written", earned: 0, max, isEquiv: false };
  return { result: "incorrect", earned: 0, max, isEquiv: false };
};

export const sumLeafEarned = (rows, ctx) =>
  rows.reduce((s, r) => s + Number(resolveLeafVerdict(r, ctx).earned ?? 0), 0);
